import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Icon } from '@iconify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { backendProvider } from '../../services/backend';
import { queryKeys } from '../../services/query/queryKeys';
import { useTeamPreferences } from '../../hooks/useTeamPreferences';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { teamPreferencesService } from '../../services/teamPreferences.service';
import { geoService } from '../../services/geo.service';
import { GeoSelect } from '../atoms/GeoSelect';
import { compressImage } from '../../utils/imageCompress';
import { PICKER_LOCALE_OPTIONS, isSupportedLocale, normalizeLocale } from '../../constants/locales';

/**
 * Settings → ACCOUNT tab. Two sections, one card each:
 *
 *   USER    — language, full name, job title, picture (editable) +
 *             email (read-only — auth identity, not editable here)
 *   COMPANY — company name + website (read-only — set at registration),
 *             billing email, country, state (editable),
 *             currency + timezone (read-only, derived from Country/State)
 *
 * USER fields persist to the user's Appwrite prefs (name → auth profile);
 * COMPANY fields persist to the Appwrite Team prefs — the same stores the
 * registration flow writes.
 */

const PROFILE_PICTURE_ACCEPT = 'image/jpeg,image/png,image/webp';
const PROFILE_PICTURE_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const PROFILE_PICTURE_MAX_DIMENSION = 400; // px — resize to this max

const getInitials = (name?: string | null): string => {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  return (words.length > 1 ? `${words[0][0]}${words[1][0]}` : words[0]?.slice(0, 2) || '?').toUpperCase();
};

/** Formats bytes as "1.2 MB" for the upload hint. */
const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

export const AccountSettingsTab: React.FC = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const { data: teamPrefs, isLoading: teamPrefsLoading } = useTeamPreferences();

  // ── USER section state ────────────────────────────────────────────────────
  const [language, setLanguage] = useState('');
  const [fullName, setFullName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [pendingPicture, setPendingPicture] = useState<{ file: File; previewUrl: string } | null>(null);

  // ── COMPANY section state ─────────────────────────────────────────────────
  const [billingEmail, setBillingEmail] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [stateName, setStateName] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const languageInitial = useRef('');
  const jobTitleInitial = useRef('');

  // Seed the form from the cached user preferences (via TanStack Query — instant).
  const { data: userPreferences } = useUserPreferences();

  // Seed the form from the signed-in user (name/email/photo).
  useEffect(() => {
    if (!currentUser) return;
    setFullName(currentUser.displayName || '');
  }, [currentUser?.uid, currentUser?.displayName]);

  // Seed user fields from cached preferences (no raw getPreferences() call).
  useEffect(() => {
    if (!userPreferences) return;
    const loadedLanguage = (userPreferences.language as string) || '';
    const loadedJobTitle = (userPreferences.job_title as string) || '';
    languageInitial.current = loadedLanguage;
    jobTitleInitial.current = loadedJobTitle;
    setLanguage(loadedLanguage);
    setJobTitle(loadedJobTitle);
  }, [userPreferences]);

  // Seed the company fields from the team prefs.
  useEffect(() => {
    if (!teamPrefs) return;
    setBillingEmail(teamPrefs.billing_email || '');
    setCountryCode(teamPrefs.country_code || '');
    setStateName(teamPrefs.state || '');
  }, [teamPrefs]);

  // Geo data — same cached queries as registration / Team tab.
  const { data: countries = [], isLoading: countriesLoading } = useQuery({
    queryKey: ['geo', 'countries'],
    queryFn: () => geoService.listCountries(),
    staleTime: Infinity,
    retry: 1,
  });
  const { data: states = [], isLoading: statesLoading } = useQuery({
    queryKey: ['geo', 'states', countryCode],
    queryFn: () => geoService.listStates(countryCode),
    enabled: Boolean(countryCode),
    staleTime: Infinity,
    retry: 1,
  });

  // Currency + timezone are DERIVED from the selected Country + State — never
  // user-selectable (same contract as registration).
  const derivedCurrency = countries.find((c) => c.iso2 === countryCode)?.currency || '';
  const derivedTimezone = (() => {
    if (!countryCode) return '';
    const stateTz = states.find((s) => s.name === stateName)?.timezone;
    if (stateTz) return stateTz;
    return countries.find((c) => c.iso2 === countryCode)?.timezones?.[0]?.zoneName || '';
  })();

  // Revoke the object URL when a staged preview is replaced/unmounted.
  useEffect(() => {
    const previewUrl = pendingPicture?.previewUrl;
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [pendingPicture?.previewUrl]);

  const handlePictureChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;
    if (!PROFILE_PICTURE_ACCEPT.includes(file.type)) {
      setError('Choose a JPG, PNG, or WebP image for your picture.');
      return;
    }
    if (file.size > PROFILE_PICTURE_MAX_BYTES) {
      setError(`That image is ${formatFileSize(file.size)} — the maximum is 5 MB.`);
      return;
    }
    setError(null);
    setPendingPicture({ file, previewUrl: URL.createObjectURL(file) });
  };

  const dirty =
    !!currentUser &&
    (fullName !== (currentUser.displayName || '') ||
      language !== languageInitial.current ||
      jobTitle !== jobTitleInitial.current ||
      billingEmail !== (teamPrefs?.billing_email || '') ||
      countryCode !== (teamPrefs?.country_code || '') ||
      stateName !== (teamPrefs?.state || '') ||
      pendingPicture !== null);

  const handleSave = async () => {
    if (!currentUser) return;
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      // 1. USER section — name via the auth profile, the rest via user prefs.
      const trimmedName = fullName.trim();
      if (trimmedName && trimmedName !== (currentUser.displayName || '')) {
        await backendProvider.auth.updateProfile({ displayName: trimmedName });
      }
      const userPayload: Record<string, any> = {};
      if (language && language !== languageInitial.current) {
        userPayload.language = isSupportedLocale(language) ? language : normalizeLocale(language);
      }
      if (jobTitle.trim() !== jobTitleInitial.current) {
        userPayload.job_title = jobTitle.trim() || null;
      }
      // Keep the user's country_code in sync with the COMPANY country selector
      // (single source of truth: geo_countries → name + iso2).
      if (countryCode && countryCode !== (userPreferences?.country_code || '')) {
        userPayload.country = countries.find((c) => c.iso2 === countryCode)?.name || null;
        userPayload.country_code = countryCode;
      }
      if (Object.keys(userPayload).length > 0) {
        await backendProvider.auth.updatePreferences(userPayload);
      }

      // 2. Picture — compress client-side then upload directly to the
      //    profile-pictures bucket. The storage URL includes a cache-bust
      //    param so the browser re-fetches the new image.
      if (pendingPicture) {
        const compressed = await compressImage(pendingPicture.file, PROFILE_PICTURE_MAX_DIMENSION);
        await backendProvider.auth.setProfilePicture(compressed);
        setPendingPicture(null);
      }

      // 3. COMPANY section — team prefs. Currency + timezone are derived from
      //    the geo data for the Country + State, exactly like registration.
      //    `country` stores the FULL name from geo_countries (code in country_code).
      const teamPayload: Parameters<typeof teamPreferencesService.updatePreferences>[0] = {
        billing_email: billingEmail || undefined,
        country: countries.find((c) => c.iso2 === countryCode)?.name || undefined,
        country_code: countryCode || undefined,
        state: stateName || undefined,
        currency: derivedCurrency || undefined,
        timezone: derivedTimezone || undefined,
      };
      await teamPreferencesService.updatePreferences(teamPayload);

      // Refresh the caches the UI reads from.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.preferences.user(currentUser.uid) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.preferences.team(currentUser.uid) }),
      ]);

      languageInitial.current = language;
      jobTitleInitial.current = jobTitle;
      setSuccessMessage('Account changes saved.');
    } catch (e: any) {
      setError(e?.message || 'Failed to save account changes');
    } finally {
      setSaving(false);
    }
  };

  const avatarUrl = pendingPicture?.previewUrl || currentUser?.photoURL || null;

  return (
    <div className="space-y-4">
      {/* ── USER ─────────────────────────────────────────────────────────── */}
      <section className="rounded-[24px] border border-white/8 bg-[linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-4 md:p-5">
        <div className="mb-3.5 flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-cyan-300/18 bg-cyan-400/10 text-cyan-100">
            <Icon icon="bx:user" className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-base font-black tracking-tight text-white">User</h3>
            <p className="mt-0.5 text-[13px] leading-5 text-gray-400">Your personal profile in this workspace.</p>
          </div>
        </div>

        {/* Identity row — big picture + name/email inline (ISR identity-panel
            pattern), so there is no dead column next to the avatar. */}
        <div className="mb-4 flex items-center gap-4">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="group relative h-24 w-24 shrink-0 overflow-hidden rounded-[24px] border border-white/12 bg-white/[0.04] transition-colors hover:border-cyan-300/40"
            title="Change your picture"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Your profile picture" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-2xl font-black text-white/60">{getInitials(currentUser?.displayName)}</span>
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-black/55 opacity-0 transition-opacity group-hover:opacity-100">
              <Icon icon="bx:camera" className="h-6 w-6 text-white" />
            </span>
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-black tracking-tight text-white">{fullName || currentUser?.email || 'User'}</p>
            <p className="mt-0.5 truncate text-[13px] text-gray-400">{currentUser?.email}</p>
            {pendingPicture ? (
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]">
                <span className="truncate font-semibold text-amber-200/90">{pendingPicture.file.name} · {formatFileSize(pendingPicture.file.size)}</span>
                <button
                  type="button"
                  onClick={() => setPendingPicture(null)}
                  className="font-semibold text-white/45 underline-offset-2 hover:text-white hover:underline"
                >
                  Remove selection
                </button>
              </div>
            ) : (
              <p className="mt-1.5 text-[11px] leading-5 text-white/35">Click the picture to change it · JPG, PNG, or WebP · max 5 MB</p>
            )}
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={PROFILE_PICTURE_ACCEPT}
          className="sr-only"
          aria-label="Choose profile picture"
          onChange={handlePictureChange}
        />

        {/* Fields — flat grid, no empty cells. */}
        <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
          {/* Full name — editable */}
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">
              <Icon icon="bx:user-pin" className="h-3.5 w-3.5" />Full name
            </span>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
              className="h-10 w-full rounded-xl border border-white/10 bg-[#111216]/85 px-3 text-sm font-semibold text-white outline-none transition-colors focus:border-cyan-300/40"
            />
          </label>

          {/* Email — read-only (auth identity) */}
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">
              <Icon icon="bx:envelope" className="h-3.5 w-3.5" />Email
            </span>
            <div className="relative">
              <input
                type="email"
                readOnly
                value={currentUser?.email || ''}
                className="h-10 w-full cursor-not-allowed rounded-xl border border-white/8 bg-black/25 px-3 pr-10 text-sm font-semibold text-white/50 outline-none"
              />
              <Icon icon="bx:lock-alt" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/25" />
            </div>
          </label>

          {/* Job title — editable */}
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">
              <Icon icon="bx:briefcase-alt" className="h-3.5 w-3.5" />Job title
            </span>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g. Head of Growth"
              className="h-10 w-full rounded-xl border border-white/10 bg-[#111216]/85 px-3 text-sm font-semibold text-white outline-none transition-colors focus:border-cyan-300/40"
            />
          </label>

          {/* Language — editable */}
          <GeoSelect
            compact
            id="user-language"
            label="Language"
            value={language}
            onChange={setLanguage}
            options={PICKER_LOCALE_OPTIONS.map((opt) => ({ value: opt.code, label: opt.label, emoji: opt.flag }))}
            placeholder="Your language…"
          />
        </div>
      </section>

      {/* ── COMPANY ──────────────────────────────────────────────────────── */}
      <section className="rounded-[24px] border border-white/8 bg-[linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-4 md:p-5">
        <div className="mb-3.5 flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-[#5260FF]/25 bg-[#5260FF]/15 text-indigo-100">
            <Icon icon="bx:buildings" className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-base font-black tracking-tight text-white">Company</h3>
            <p className="mt-0.5 text-[13px] leading-5 text-gray-400">
              Shared company profile. Name and website are set at registration.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
          {/* Company name — read-only (Appwrite Team name) */}
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">
              <Icon icon="bx:building-house" className="h-3.5 w-3.5" />Company name
            </span>
            <div className="relative">
              <input
                type="text"
                readOnly
                value={teamPrefs?.team_name || ''}
                className="h-10 w-full cursor-not-allowed rounded-xl border border-white/8 bg-black/25 px-3 pr-10 text-sm font-semibold text-white/50 outline-none"
              />
              <Icon icon="bx:lock-alt" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/25" />
            </div>
          </label>

          {/* Website — read-only */}
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">
              <Icon icon="bx:globe" className="h-3.5 w-3.5" />Website
            </span>
            <div className="relative">
              <input
                type="url"
                readOnly
                value={teamPrefs?.company_website || ''}
                className="h-10 w-full cursor-not-allowed rounded-xl border border-white/8 bg-black/25 px-3 pr-10 text-sm font-semibold text-white/50 outline-none"
              />
              <Icon icon="bx:lock-alt" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/25" />
            </div>
          </label>

          {/* Billing email — editable */}
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">
              <Icon icon="bx:envelope" className="h-3.5 w-3.5" />Billing email
            </span>
            <input
              type="email"
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
              placeholder="billing@yourcompany.com"
              className="h-10 w-full rounded-xl border border-white/10 bg-[#111216]/85 px-3 text-sm font-semibold text-white outline-none transition-colors focus:border-cyan-300/40"
            />
          </label>

          <GeoSelect
            compact
            id="account-country"
            label="Country"
            value={countryCode}
            onChange={(iso2) => { setCountryCode(iso2); setStateName(''); }}
            options={countries.map((c) => ({ value: c.iso2, label: c.name, emoji: c.emoji }))}
            placeholder="Select country…"
            loading={countriesLoading}
          />
          <GeoSelect
            compact
            id="account-state"
            label="State / Province"
            value={stateName}
            onChange={setStateName}
            options={states.map((s) => ({ value: s.name, label: s.name }))}
            placeholder="Select state…"
            loading={statesLoading}
            disabled={!countryCode}
          />
        </div>

        {/* Derived localization — flat hairline row, read-only by design. */}
        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1.5 border-t border-white/[0.06] pt-3 text-[13px]">
          <span className="flex items-center gap-2 text-white/40">
            <Icon icon="bx:coin" className="h-3.5 w-3.5" />Currency
            <b className="font-bold text-white/85">{derivedCurrency || '—'}</b>
          </span>
          <span className="flex items-center gap-2 text-white/40">
            <Icon icon="bx:time" className="h-3.5 w-3.5" />Timezone
            <b className="font-bold text-white/85">{derivedTimezone || '—'}</b>
          </span>
        </div>
      </section>

      {/* Save footer — STICKY: always visible without scrolling. The messages
          live here (not in the scroll area) so feedback is never missed. */}
      <div className="sticky bottom-0 z-10 -mx-4 mt-4 flex items-center justify-between gap-3 border-t border-white/8 bg-[#0d0e12]/95 px-4 py-3 backdrop-blur-md md:-mx-6 md:px-6">
        <div className="min-w-0 text-xs" aria-live="polite">
          {error ? (
            <span className="flex items-center gap-1.5 font-semibold text-rose-300">
              <Icon icon="bx:error-circle" className="h-4 w-4 shrink-0" /><span className="truncate">{error}</span>
            </span>
          ) : successMessage ? (
            <span className="flex items-center gap-1.5 font-semibold text-emerald-300">
              <Icon icon="bx:check-circle" className="h-4 w-4 shrink-0" /><span className="truncate">{successMessage}</span>
            </span>
          ) : (
            <span className="text-white/35">{dirty ? 'Unsaved changes' : 'All information is up to date'}</span>
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border px-4 text-sm font-black text-white shadow-[0_16px_32px_rgba(82,96,255,0.24)] transition-all disabled:cursor-not-allowed ${
            dirty && !saving
              ? 'border-[#5260FF]/30 bg-[#5260FF] hover:bg-[#4553f1]'
              : 'border-white/8 bg-white/[0.06] shadow-none opacity-50'
          }`}
        >
          {saving ? <Icon icon="bx:loader-circle" className="h-4 w-4 animate-spin" /> : <Icon icon="bx:save" className="h-4 w-4" />}
          {saving ? 'Saving…' : 'Save account'}
        </button>
      </div>
    </div>
  );
};

export default AccountSettingsTab;
